// src/plans.ts
var PLAN_IDS = ["free", "starter", "pro", "business"];
var CAPABILITIES = [
  "aiInvestigator",
  // AI Investigator (all plans, incl. Free)
  "auditLog",
  // audit log (all plans)
  "alerts",
  // alert rules & notifications
  "pipelines",
  // visual pipeline builder
  "aiPipelineAgent",
  // AI Pipeline Agent
  "anomalyDetection",
  // anomaly detection
  "siem",
  // Security Monitoring (detections, MITRE, risk, SOAR, …)
  "healthChecks",
  // uptime/health checks (all plans, incl. Free)
  "serviceIntelligence",
  // service model, KPIs, entities, dependencies
  "piiRedaction",
  // PII redaction & compliance pipelines
  "detectionLab",
  // Detection Lab + NIS2/ISO 27001 coverage report (Intelligence Hub, Pro+)
  "sso",
  // SSO / SAML (Enterprise — no self-serve plan grants it)
  "dedicatedStack"
  // fully dedicated stack (Enterprise)
];
var RESOURCE_KEYS = [
  "agents",
  "indexes",
  "apiKeys",
  "alertRules",
  "pipelines",
  "dashboards",
  "savedQueries",
  "scheduledExports",
  "cloudIntegrations",
  // Service Intelligence: every KPI runs a query on a schedule, so both are capped.
  "services",
  "kpis"
];
var UNLIMITED = -1;
function isUnlimited(n) {
  return n < 0;
}
var PLANS = {
  free: {
    id: "free",
    name: "Free",
    priceEur: 0,
    tier: 0,
    mbPerDay: 1024,
    // 7 days (ADR-0106): enough to investigate last week, not a free archive.
    retentionDays: 7,
    cloudHealthChecksPerDay: 1500,
    maxMembers: 2,
    aiCreditsPerMonth: 100,
    features: [
      "1 GB/day ingestion",
      "7 days retention",
      "2 team members",
      "Basic search (LPQL)",
      "Visual pipeline builder",
      "Uptime & health checks",
      "Security monitoring (SIEM)",
      "Observability: services, anomalies & alerts",
      "MCP for coding agents"
    ],
    // Free carries the whole loop a small app needs once it ships: security
    // monitoring, observability, alerts and the MCP server with proposals, so a
    // coding agent can watch what it built (ADR-0105). Paid plans sell volume,
    // retention, scale and compliance. The AI Pipeline Agent (aiPipelineAgent)
    // and PII/compliance pipelines (piiRedaction) stay paid.
    capabilities: ["aiInvestigator", "auditLog", "alerts", "pipelines", "anomalyDetection", "healthChecks", "siem", "serviceIntelligence"],
    limits: { agents: 5, indexes: 1, apiKeys: 5, alertRules: 5, pipelines: 3, dashboards: 1, savedQueries: 5, scheduledExports: 0, cloudIntegrations: 1, services: 3, kpis: 15 },
    adhocEvaluation: { perMinute: 10, concurrent: 2 },
    mcpLevel: "propose",
    support: "community",
    color: "emerald",
    mollieDescription: "LogPulse Free",
    mollieAmount: { currency: "EUR", value: "0.00" }
  },
  // LEGACY (grandfathered) — no longer offered on the pricing page since Free
  // was raised to 1 GB/day. Kept in the enum so existing subscribers and
  // billing validation keep working; do not surface in new signup flows.
  starter: {
    id: "starter",
    name: "Starter",
    priceEur: 29,
    tier: 1,
    mbPerDay: 1024,
    retentionDays: 14,
    cloudHealthChecksPerDay: 3e4,
    maxMembers: 3,
    aiCreditsPerMonth: 1e3,
    features: [
      "1 GB/day ingestion",
      "14 days retention",
      "3 team members",
      "LPQL + dashboards"
    ],
    // Grandfathered subscribers never get less than Free.
    capabilities: ["aiInvestigator", "auditLog", "alerts", "pipelines", "anomalyDetection", "healthChecks", "siem", "serviceIntelligence"],
    limits: { agents: 10, indexes: 5, apiKeys: 5, alertRules: 10, pipelines: 3, dashboards: 5, savedQueries: 20, scheduledExports: 1, cloudIntegrations: UNLIMITED, services: 3, kpis: 15 },
    adhocEvaluation: { perMinute: 10, concurrent: 2 },
    mcpLevel: "propose",
    support: "community",
    color: "blue",
    mollieDescription: "LogPulse Starter",
    mollieAmount: { currency: "EUR", value: "29.00" }
  },
  pro: {
    id: "pro",
    name: "Pro",
    priceEur: 59,
    tier: 2,
    mbPerDay: 10240,
    retentionDays: 60,
    cloudHealthChecksPerDay: 3e4,
    maxMembers: 10,
    aiCreditsPerMonth: 2500,
    features: [
      "10 GB/day ingestion",
      "60 days retention",
      "10 team members",
      "Alerts + integrations",
      "Security monitoring + observability at team scale"
    ],
    capabilities: ["aiInvestigator", "auditLog", "alerts", "pipelines", "aiPipelineAgent", "anomalyDetection", "healthChecks", "siem", "serviceIntelligence", "detectionLab"],
    limits: { agents: 100, indexes: 25, apiKeys: 20, alertRules: 50, pipelines: 10, dashboards: 20, savedQueries: 100, scheduledExports: 5, cloudIntegrations: UNLIMITED, services: 25, kpis: 150 },
    adhocEvaluation: { perMinute: 20, concurrent: 3 },
    mcpLevel: "propose",
    support: "community",
    color: "purple",
    mollieDescription: "LogPulse Pro",
    mollieAmount: { currency: "EUR", value: "59.00" }
  },
  business: {
    id: "business",
    name: "Business",
    priceEur: 199,
    tier: 3,
    mbPerDay: 51200,
    retentionDays: 90,
    cloudHealthChecksPerDay: UNLIMITED,
    maxMembers: -1,
    aiCreditsPerMonth: 1e4,
    features: [
      "50 GB/day ingestion",
      "90 days retention",
      "Unlimited team members",
      "PII redaction & compliance pipelines",
      "Priority support + SLA"
    ],
    capabilities: ["aiInvestigator", "auditLog", "alerts", "pipelines", "aiPipelineAgent", "anomalyDetection", "healthChecks", "siem", "serviceIntelligence", "piiRedaction", "detectionLab"],
    limits: { agents: UNLIMITED, indexes: 100, apiKeys: 50, alertRules: 200, pipelines: 100, dashboards: 100, savedQueries: 500, scheduledExports: 20, cloudIntegrations: UNLIMITED, services: 200, kpis: 1500 },
    adhocEvaluation: { perMinute: 30, concurrent: 5 },
    mcpLevel: "propose",
    support: "priority",
    color: "amber",
    mollieDescription: "LogPulse Business",
    mollieAmount: { currency: "EUR", value: "199.00" }
  }
};
var PLAN_LIST = PLAN_IDS.map((id) => PLANS[id]);
var QUOTA_HARD_LIMIT_FACTOR = 1.25;
var AUDIT_RETENTION_DAYS = 180;
var AI_CREDIT_RATE = 100;
var MICRO_CREDITS_PER_CREDIT = 1e6;
var AI_OVERAGE_PRICE_PER_CREDIT_CENTS = 1.8;
function microCreditsFromCostUsd(costUsd) {
  if (!Number.isFinite(costUsd) || costUsd <= 0) return 0;
  return Math.max(1, Math.ceil(costUsd * AI_CREDIT_RATE * MICRO_CREDITS_PER_CREDIT));
}
function creditsFromMicroCredits(microCredits) {
  return microCredits / MICRO_CREDITS_PER_CREDIT;
}
function getPlan(planId) {
  return PLANS[planId] ?? PLANS.free;
}
function getPlanPrice(planId) {
  return getPlan(planId).priceEur;
}
function getPlanLimitBytes(planId) {
  return getPlan(planId).mbPerDay * 1024 * 1024;
}
function planHasCapability(planId, cap) {
  return getPlan(planId).capabilities.includes(cap);
}
function getResourceLimit(planId, resource) {
  return getPlan(planId).limits[resource];
}
function getCloudHealthCheckLimit(planId) {
  return getPlan(planId).cloudHealthChecksPerDay;
}
function cloudHealthCheckCounterKey(orgId, dayKey) {
  return `quota:healthchecks:cloud:${orgId}:${dayKey}`;
}
function effectivePlanId(plan, trialPlan, trialEndsAt, now = /* @__PURE__ */ new Date()) {
  const trialActive = !!(trialPlan && trialEndsAt && trialEndsAt.getTime() > now.getTime());
  return trialActive ? trialPlan : plan ?? "free";
}
function formatPlanLimit(planId) {
  const mb = getPlan(planId).mbPerDay;
  return mb >= 1024 ? `${(mb / 1024).toFixed(0)} GB/day` : `${mb} MB/day`;
}
function formatPlanPrice(planId) {
  const price = getPlanPrice(planId);
  return price === 0 ? "Free" : `\u20AC${price}/mo`;
}
function comparePlanTier(a, b) {
  const tierA = getPlan(a).tier;
  const tierB = getPlan(b).tier;
  if (tierA > tierB) return 1;
  if (tierA < tierB) return -1;
  return 0;
}
var PAID_PLAN_IDS = PLAN_IDS.filter((id) => id !== "free");
function nextMonthStart(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1));
}
function monthStart(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}
function planShareCents(priceEur, from, to, periodStart, periodEnd) {
  const start = Math.max(from.getTime(), periodStart.getTime());
  const end = Math.min(to.getTime(), periodEnd.getTime());
  if (end <= start) return 0;
  return Math.round(priceEur * 100 * (end - start) / (periodEnd.getTime() - periodStart.getTime()));
}
function calculateProration(oldPlanId, newPlanId, now = /* @__PURE__ */ new Date()) {
  const oldPlan = getPlan(oldPlanId);
  const newPlan = getPlan(newPlanId);
  const periodStart = monthStart(now);
  const periodEnd = nextMonthStart(now);
  const dayMs = 24 * 60 * 60 * 1e3;
  const daysInMonth = Math.round((periodEnd.getTime() - periodStart.getTime()) / dayMs);
  const credit = planShareCents(oldPlan.priceEur, now, periodEnd, periodStart, periodEnd) / 100;
  const charge = planShareCents(newPlan.priceEur, now, periodEnd, periodStart, periodEnd) / 100;
  const proratedAmount = Math.max(0, Math.round((charge - credit) * 100) / 100);
  return {
    proratedAmount,
    proratedAmountFormatted: proratedAmount.toFixed(2),
    daysRemaining: Math.ceil((periodEnd.getTime() - now.getTime()) / dayMs),
    credit,
    charge,
    dailyRateOld: Math.round(oldPlan.priceEur / daysInMonth * 100) / 100,
    dailyRateNew: Math.round(newPlan.priceEur / daysInMonth * 100) / 100
  };
}
function planSegments(changes, planBeforeHistory, periodStart, periodEnd) {
  const sorted = [...changes].sort((a, b) => a.effectiveAt.getTime() - b.effectiveAt.getTime());
  let current = planBeforeHistory;
  for (const c of sorted) {
    if (c.effectiveAt.getTime() <= periodStart.getTime()) current = c.plan;
  }
  const segments = [];
  let from = periodStart;
  for (const c of sorted) {
    const at = c.effectiveAt.getTime();
    if (at <= periodStart.getTime() || at >= periodEnd.getTime()) continue;
    if (c.plan === current) continue;
    segments.push({ planId: current, from, to: c.effectiveAt });
    current = c.plan;
    from = c.effectiveAt;
  }
  segments.push({ planId: current, from, to: periodEnd });
  return segments.filter((s) => s.to.getTime() > s.from.getTime());
}
var CAPABILITY_LABELS = {
  aiInvestigator: "AI Investigator",
  auditLog: "Audit log",
  alerts: "Alerts & notifications",
  pipelines: "Visual pipeline builder",
  aiPipelineAgent: "AI Pipeline Agent",
  anomalyDetection: "Anomaly detection",
  healthChecks: "Uptime & health checks",
  siem: "Security monitoring (SIEM)",
  serviceIntelligence: "Service Intelligence",
  piiRedaction: "PII redaction & compliance pipelines",
  detectionLab: "Detection Lab & coverage report",
  sso: "SSO / SAML",
  dedicatedStack: "Dedicated stack"
};
function volume(mbPerDay) {
  return mbPerDay >= 1024 ? `${Math.round(mbPerDay / 1024)} GB/day` : `${mbPerDay} MB/day`;
}
function planDowngradeLosses(fromPlanId, toPlanId) {
  const from = getPlan(fromPlanId);
  const to = getPlan(toPlanId);
  const kept = new Set(to.capabilities);
  const lines = from.capabilities.filter((c) => !kept.has(c)).map((c) => CAPABILITY_LABELS[c] ?? c);
  if (from.mbPerDay > to.mbPerDay) lines.push(`${volume(from.mbPerDay)} ingestion (${volume(to.mbPerDay)} on ${to.name})`);
  if (from.retentionDays > to.retentionDays) lines.push(`${from.retentionDays} days retention (${to.retentionDays} on ${to.name})`);
  const members = (n) => n < 0 ? Infinity : n;
  if (members(from.maxMembers) > members(to.maxMembers)) {
    lines.push(`${from.maxMembers < 0 ? "Unlimited" : `Up to ${from.maxMembers}`} team members (${to.maxMembers} on ${to.name})`);
  }
  if (from.support !== to.support && from.support === "priority") lines.push("Priority support");
  return lines;
}

export {
  PLAN_IDS,
  CAPABILITIES,
  RESOURCE_KEYS,
  UNLIMITED,
  isUnlimited,
  PLANS,
  PLAN_LIST,
  QUOTA_HARD_LIMIT_FACTOR,
  AUDIT_RETENTION_DAYS,
  AI_CREDIT_RATE,
  MICRO_CREDITS_PER_CREDIT,
  AI_OVERAGE_PRICE_PER_CREDIT_CENTS,
  microCreditsFromCostUsd,
  creditsFromMicroCredits,
  getPlan,
  getPlanPrice,
  getPlanLimitBytes,
  planHasCapability,
  getResourceLimit,
  getCloudHealthCheckLimit,
  cloudHealthCheckCounterKey,
  effectivePlanId,
  formatPlanLimit,
  formatPlanPrice,
  comparePlanTier,
  PAID_PLAN_IDS,
  nextMonthStart,
  monthStart,
  planShareCents,
  calculateProration,
  planSegments,
  CAPABILITY_LABELS,
  planDowngradeLosses
};
